import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgSelectOption,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import {
  NgOptgroupTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';
import { formatDisplayText, formatValueSafely } from '../../utils/text-case.utils';

@Component({
  selector: 'app-global-dropdown',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPseudoCheckboxModule,
    NgSelectComponent,
    FormsModule,
    NgOptionTemplateDirective,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlobalDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './global-dropdown.component.html',
  styleUrl: './global-dropdown.component.scss',
})
export class GlobalDropdownComponent implements ControlValueAccessor, OnChanges {
  @Input() data: any;
  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();
  private suppressSelectionChange = false;
  private destroy$ = new Subject<void>();

  selected: any[] = [];
  searchCtrl = new FormControl('');
  filteredOptions: any[] = [];
  isDropdownOpen = false;

  selectedDropdownList: { [key: string]: any[] } = {};

  private lastFetchSignature: string | null = null;

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters: any) => {
        if (filters?.resetFilterControls !== true) {
          return;
        }

        this.selected = [];
        this.searchCtrl.setValue('', { emitEvent: false });
        this.onChangeFn(this.data?.isMultiSelect ? [] : null);
      });

    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText: string | null) => {
        const term = (searchText || '').toLowerCase();
        if (!term) {
          this.filteredOptions = [...this.options];
        } else {
          this.filteredOptions = this.options.filter((opt) => opt.toLowerCase().includes(term));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.data?.apiUrl) {
      const signature = this.buildFetchSignature(this.data);
      if (signature === this.lastFetchSignature) {
        return;
      }
      this.lastFetchSignature = signature;

      this.configService.getConfig(this.data?.apiUrl, this.data,this.data.method).subscribe({
        next: (res) => {
          this.options = this.normalizeDropdownOptions(res?.data);
          this.filteredOptions = this.options;
          this.data.optionsList = this.options;
        },
        error: (err) => console.error(err),
      });
    } else {
      this.options = this.normalizeDropdownOptions(this.options);
      this.filteredOptions = this.options;
    }
  }

  // `data` is rebuilt with a new object reference on every filter-sync tick in the parent
  // (GlobalFilterComponent), even when nothing about THIS dropdown's request actually changed —
  // which used to refire this fetch on every tick. Comparing the request-relevant shape (everything
  // except fields we know are re-derived UI state, not request input) skips those no-op refetches
  // without risking a missed refetch for a config field we don't explicitly know about.
  private buildFetchSignature(data: any): string {
    const { optionsList, disabled, disabledPlaceholder, ...requestRelevant } = data || {};
    try {
      return JSON.stringify(requestRelevant);
    } catch {
      return String(data?.apiUrl || '');
    }
  }

  get isAllSelected(): boolean {
    if (!Array.isArray(this.options) || !Array.isArray(this.selected)) {
      return false;
    }

    // When a parent filter narrows a dropdown to a single remaining option,
    // selecting that one value should not visually imply that the user chose
    // "Select All". Keep the checkbox unchecked for 0/1 available options.
    if (this.options.length <= 1) {
      return false;
    }

    return this.selected.length === this.options.length;
  }

  get placeholderText(): string {
    return this.data?.disabled ? (this.data?.disabledPlaceholder || 'Select a value first') : 'All';
  }

  isItemSelected(item: any): boolean {
    return this.selected?.includes(item);
  }

  toggleSelectAll(event: any): void {
    if (this.data?.disabled) {
      return;
    }
    const isChecked = event?.target?.checked;

    // Prevent triggering onChange again
    this.suppressSelectionChange = true;
    // this.selected = isChecked ? [...this.filteredOptions] : [];
    this.selected = isChecked ? [...this.options] : [];

    // Emit manually once
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;
      const filterListener = this.data?.filterListener || null;
      const isEmptySelection = Array.isArray(valueToEmit)
        ? valueToEmit.length === 0
        : valueToEmit === null || valueToEmit === undefined || valueToEmit === '';

      if (isEmptySelection) {
        this.globalFilterService.resetFilterSelection(
          this.data.key,
          this.data?.isQueryParam,
          filterListener,
        );
      } else {
        this.globalFilterService.updateFilterSelection(
          this.data.key,
          valueToEmit,
          this.data?.isQueryParam,
          undefined,
          true,
          filterListener,
        );
      }
    }

    this.filterChanged.emit(valueToEmit);

    setTimeout(() => {
      this.suppressSelectionChange = false;
    }, 0);
  }

  onChange(event: any): void {
    if (this.data?.disabled) return;
    if (this.suppressSelectionChange) return;
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data?.key;
      const filterListener = this.data?.filterListener || null;
      const isEmptySelection = Array.isArray(valueToEmit)
        ? valueToEmit.length === 0
        : valueToEmit === null || valueToEmit === undefined || valueToEmit === '';

      if (isEmptySelection) {
        this.globalFilterService.resetFilterSelection(
          this.data.key,
          this.data?.isQueryParam,
          filterListener,
        );
      } else {
        this.globalFilterService.updateFilterSelection(
          this.data?.key,
          valueToEmit,
          this.data?.isQueryParam,
          undefined,
          true,
          filterListener,
        );
      }
    }

    this.filterChanged.emit(valueToEmit);
  }

  onItemClick(item: any): void {
    if (this.data?.disabled) {
      return;
    }
    if (!this.data?.isMultiSelect) {
      if (this.selected === item) {
        // Unselect if same item clicked again
        this.selected = [];
        this.onChangeFn(this.selected);
        this.onTouchedFn();

        const valueToEmit = this.selected;

        if (this.data?.key) {
          this.globalFilterService.activeFilter = this.data?.key;
          // Pass filterListener if available
          const filterListener = this.data?.filterListener || null;
          this.globalFilterService.updateFilterSelection(
            this.data.key,
            valueToEmit,
            this.data?.isQueryParam,
            undefined,
            true,
            filterListener,
          );
        }

        this.filterChanged.emit(valueToEmit);
      }
    }
  }

  removeSelected(item: any): void {
    this.selected = this.selected.filter((i) => i !== item);
    this.onChange(this.selected); // emit the change
  }

  onDropdownClose() {
    this.isDropdownOpen = false;
    this.searchCtrl.setValue('', { emitEvent: true }); // clears input
  }

  onDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  writeValue(value: any): void {
    if (this.data?.isMultiSelect) {
      if (Array.isArray(value)) {
        this.selected = value;
        return;
      }

      if (value === null || value === undefined || value === '') {
        this.selected = [];
        return;
      }

      this.selected = [value];
      return;
    }

    this.selected = value ?? [];
  }

  private normalizeDropdownOptions(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [value];
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  /**
   * Display-formatting helpers for template.
   * Per UI Standards (Point 3): filter/dropdown names and values in Capital Case.
   * The textCase can be overridden per-widget via `data.textCase` config.
   */
  formatLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatValueSafely(value, mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
